import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take } from 'rxjs/operators';

import { select, Store } from '@ngrx/store';
import { PayRollService } from '@services';
import { selectRouteState } from '@state/route';
import * as PayRollActions from './pay-roll.actions';

@Injectable()
export class PayRollEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private payRollService: PayRollService
  ) {}

  uploadPayRollBatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayRollActions.uploadPayRollBatch),
      switchMap(({ file }) =>
        from(this.payRollService.uploadPayRollBatch(file)).pipe(
          map((message) =>
            PayRollActions.uploadPayRollBatchSuccess({ message })
          ),
          catchError((error) =>
            of(PayRollActions.uploadPayRollBatchFailed({ error }))
          )
        )
      )
    )
  );

  processPayRollBatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayRollActions.processPayRollBatch),
      switchMap(({ payRollBatchId }) =>
        from(this.payRollService.processPayRollBatch(payRollBatchId)).pipe(
          map((message) =>
            PayRollActions.processPayRollBatchSuccess({ message })
          ),
          catchError((error) =>
            of(PayRollActions.processPayRollBatchFailed({ error }))
          )
        )
      )
    )
  );

  fetchPayRollBatches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayRollActions.fetchPayRollBatches),
      switchMap(() =>
        from(this.payRollService.getPayRollBatches()).pipe(
          map((payRollBatches) =>
            PayRollActions.fetchPayRollBatchesSuccess({ payRollBatches })
          ),
          catchError((error) =>
            of(PayRollActions.fetchPayRollBatchesFailed({ error }))
          )
        )
      )
    )
  );

  fetchPayRollBatchById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayRollActions.fetchCurrentPayRollBatchById),
      mergeMap(() => this.store.pipe(select(selectRouteState), take(1))),
      switchMap((route) => {
        const payRollBatchId = route.params['payRollBatchId'];
        return from(
          this.payRollService.getPayRollBatchById(payRollBatchId)
        ).pipe(
          map((payRollBatch) =>
            PayRollActions.fetchCurrentPayRollBatchByIdSuccess({
              payRollBatch,
            })
          ),
          catchError((error) =>
            of(PayRollActions.fetchCurrentPayRollBatchByIdFailed({ error }))
          )
        );
      })
    )
  );

  fetchCurrentPayRollById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PayRollActions.fetchCurrentPayRollById),
      mergeMap(() => this.store.pipe(select(selectRouteState), take(1))),
      switchMap((route) => {
        const payRollId = route.params['payRollId'];
        return from(this.payRollService.getPayRollById(payRollId)).pipe(
          map((payRoll) =>
            PayRollActions.fetchCurrentPayRollByIdSuccess({ payRoll })
          ),
          catchError((error) =>
            of(PayRollActions.fetchCurrentPayRollByIdFailed({ error }))
          )
        );
      })
    )
  );
}
